import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getQaQueue, submitQaCheck, deleteRepair } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { canManageRepairs, isTechnician, isTechnicalCoordinator, isAdminOfficer, canDelete } from '../../utils/auth';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportStatus } from '../../utils/tableExport';

const canQa = () =>
  isTechnician() || canManageRepairs() || isTechnicalCoordinator() || isAdminOfficer();

const RepairQaPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [remarks, setRemarks] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getQaQueue();
      setRows(res.data || []);
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          err?.response?.status === 404
            ? 'QA API not found (404). Upload latest backend/src to Contabo and restart PM2 (erp-backend).'
            : 'Failed to load QA queue'
        )
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleQa = async (row, result) => {
    const remark = (remarks[row.id] || '').trim();
    if (result === 'fail' && !remark) {
      setError('Enter a remark when QA fails');
      return;
    }
    try {
      setBusyId(row.id);
      setError('');
      await submitQaCheck(row.id, { result, remark });
      setSuccess(result === 'pass' ? 'QA passed — Ready to Collect' : 'QA failed — remains on queue');
      setRemarks((prev) => ({ ...prev, [row.id]: '' }));
      load();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to submit QA'));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (row) => {
    if (!canDelete()) return;
    const label = row.grn_number || row.service_number || `#${row.id}`;
    if (!window.confirm(`Delete job ${label}?`)) return;
    try {
      setBusyId(row.id);
      await deleteRepair(row.id);
      setSuccess(`Deleted ${label}`);
      load();
    } catch (err) {
      setError(getErrorMessage(err, 'Delete failed'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>QA Check</Typography>
          <Typography variant="body2" color="text.secondary">
            Jobs awaiting quality check after outsource return. Pass → Ready to Collect; Fail → stays here with remark.
          </Typography>
        </Box>
        <ExportButtons
          title="QA Queue"
          filename="qa_queue"
          landscape
          columns={[
            { label: 'JOB #', value: (r) => dash(r.grn_number || r.service_number || r.repair_number) },
            { label: 'Customer', value: (r) => dash(r.customer_name) },
            { label: 'Phone', value: (r) => dash(r.customer_phone) },
            { label: 'Device', value: (r) => [r.device_brand, r.device_model].filter(Boolean).join(' ') || '-' },
            { label: 'Type', value: (r) => fmtExportStatus(r.repair_location_type || 'in_house') },
            { label: 'Outsource Tech', value: (r) => dash(r.outsource_technician_name) },
            { label: 'QA Status', value: (r) => fmtExportStatus(r.qa_status || 'pending') },
            { label: 'QA Remark', value: (r) => dash(r.qa_remark) }
          ]}
          rows={rows}
          onError={setError}
        />
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {!canQa() && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Only in-house technicians and repair managers can submit QA results.
        </Alert>
      )}
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>JOB #</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Outsource Tech</TableCell>
                <TableCell>QA Status</TableCell>
                <TableCell>Remark / Action</TableCell>
                {canDelete() && <TableCell>Delete</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canDelete() ? 8 : 7} align="center">No jobs awaiting QA</TableCell>
                </TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Chip
                      label={r.grn_number || r.service_number || r.repair_number}
                      size="small"
                      color="primary"
                      onClick={() => navigate(`/repairs/${r.id}`)}
                      sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>
                    {r.customer_name}
                    <br />
                    <Typography variant="caption">{r.customer_phone}</Typography>
                  </TableCell>
                  <TableCell>{r.device_brand} {r.device_model}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={(r.repair_location_type || 'in_house').replace(/_/g, ' ')}
                      color={r.repair_location_type === 'outsource' ? 'secondary' : r.repair_location_type === 'both' ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{r.outsource_technician_name || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.qa_status || 'pending'}
                      color={r.qa_status === 'failed' ? 'error' : 'warning'}
                    />
                    {r.qa_remark && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Prev: {r.qa_remark}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 260 }}>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={1}
                      placeholder="Check remark"
                      value={remarks[r.id] || ''}
                      onChange={(e) => setRemarks((prev) => ({ ...prev, [r.id]: e.target.value }))}
                      sx={{ mb: 1 }}
                      disabled={!canQa() || busyId === r.id}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={!canQa() || busyId === r.id}
                        onClick={() => handleQa(r, 'pass')}
                      >
                        {busyId === r.id ? '...' : 'OK → Collect'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={!canQa() || busyId === r.id}
                        onClick={() => handleQa(r, 'fail')}
                      >
                        Fail
                      </Button>
                    </Box>
                  </TableCell>
                  {canDelete() && (
                    <TableCell>
                      <IconButton size="small" color="error" disabled={busyId === r.id} onClick={() => handleDelete(r)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default RepairQaPage;
